import {
  offersRef,
  serverTimestamp,
  getOrCreateConversation,
  getMessagesRef,
  getConversationRef,
  getProductRef,
} from './collections';
import { createNotification } from './notifications';
import { firebase } from './config';

const OFFER_EXPIRY_MS = 48 * 60 * 60 * 1000;

export async function createOffer({
  productId,
  productName,
  productImage,
  sellerId,
  buyerId,
  buyerName,
  buyerAvatar,
  offerAmount,
  originalPrice,
  message,
  paymentMethod,
  deliveryPreference,
  meetingTime,
  needId,
  variant,
  itemTitle,
  itemCondition,
  exchangeOffer,
}) {
  const expiresAt = new Date(Date.now() + OFFER_EXPIRY_MS);
  const amt = Number(offerAmount) || 0;

  // 1. Create the offer document
  const offerRef = await offersRef().add({
    productId,
    productName: productName || '',
    productImage: productImage || '',
    buyerId,
    buyerName: buyerName || '',
    buyerAvatar: buyerAvatar || '',
    sellerId,
    offerAmount: amt,
    originalPrice: Number(originalPrice) || 0,
    status: 'pending',
    counterAmount: null,
    lastOfferBy: buyerId,
    counterBy: null,
    variant: variant || 'monetary',
    itemTitle: itemTitle || null,
    itemCondition: itemCondition || null,
    exchangeOffer: exchangeOffer || null,
    message: message || '',
    paymentMethod: paymentMethod || '',
    deliveryPreference: deliveryPreference || '',
    meetingTime: meetingTime || null,
    needId: needId || null,
    negotiationHistory: [
      {
        amount: amt,
        by: buyerId,
        timestamp: new Date(),
        message: message || '',
      },
    ],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    expiresAt: expiresAt,
  });

  const offerId = offerRef.id;

  try {
    await getProductRef(productId).update({
      offersCount: firebase.firestore.FieldValue.increment(1),
    });
  } catch (err) {
    console.error('createOffer: failed to update offersCount', err);
  }

  // 2. Get or create a conversation and send the offer as a chat message
  try {
    const conversationId = await getOrCreateConversation(
      buyerId,
      sellerId,
      productId,
      productName,
      productImage,
      Number(originalPrice) || 0
    );

    if (conversationId) {
      const offerText = `Você fez uma oferta de R$ ${amt.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
      await getMessagesRef(conversationId).add({
        senderId: buyerId,
        text: message ? `${offerText}\n"${message}"` : offerText,
        type: 'offer',
        offerAmount: amt,
        originalPrice: Number(originalPrice) || 0,
        offerId,
        timestamp: serverTimestamp(),
        read: false,
      });
      await getConversationRef(conversationId).update({
        lastMessage: offerText,
        lastMessageSender: buyerId,
        lastMessageType: 'offer',
        lastMessageAt: serverTimestamp(),
      });
    }
  } catch (err) {
    console.error('createOffer: failed to send chat message', err);
    // Don't fail the offer if chat message fails
  }

  // 3. Send notification to seller
  createNotification(
    sellerId,
    'offer',
    'Nova oferta recebida',
    `Alguém ofereceu R$ ${amt.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} para "${productName || 'seu anúncio'}"`,
    { productId, offerId }
  );

  return offerId;
}

export function updateOfferStatus(offerId, status, counterAmount) {
  const doc = offersRef().doc(offerId);
  const updates = {
    status,
    updatedAt: serverTimestamp(),
  };
  if (counterAmount != null) updates.counterAmount = Number(counterAmount);
  return doc.update(updates);
}

export async function counterOffer(
  offerId,
  counterAmount,
  sellerId,
  productId,
  productName
) {
  const amt = Number(counterAmount);
  if (!amt || amt <= 0) throw new Error('Valor de contraoferta inválido');

  const offerDoc = await offersRef().doc(offerId).get();
  const offerData = offerDoc.data() || {};
  const buyerId = offerData.buyerId;

  const updates = {
    status: 'countered',
    counterAmount: amt,
    counterBy: sellerId,
    lastOfferBy: sellerId,
    updatedAt: serverTimestamp(),
    negotiationHistory: [
      ...(offerData.negotiationHistory || []),
      {
        amount: amt,
        by: sellerId,
        timestamp: new Date(),
        message: 'Contraoferta enviada',
      },
    ],
  };

  await offersRef().doc(offerId).update(updates);

  if (buyerId) {
    createNotification(
      buyerId,
      'offer',
      'Contraoferta recebida',
      `O vendedor enviou uma contraproposta de R$ ${amt.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} para "${productName || 'seu anúncio'}"`,
      { productId, offerId }
    );
  }

  return offerId;
}

export async function acceptOffer(
  offerId,
  productId,
  buyerId,
  sellerId,
  productName,
  productImage
) {
  const productDoc = await getProductRef(productId).get();
  const productData = productDoc.data() || {};
  const isNeed =
    productData.postType === 'need' || productData.adType === 'wanted';

  const updates = { status: 'accepted', updatedAt: serverTimestamp() };
  await offersRef().doc(offerId).update(updates);

  if (!isNeed) {
    await offersRef()
      .where('productId', '==', productId)
      .where('status', '==', 'pending')
      .get()
      .then((snap) => {
        const batch = [];
        snap.docs.forEach((d) => {
          if (d.id !== offerId)
            batch.push(
              d.ref.update({ status: 'rejected', updatedAt: serverTimestamp() })
            );
        });
        return Promise.all(batch);
      });
  }

  const conversationId = await getOrCreateConversation(
    buyerId,
    sellerId,
    productId,
    productName,
    productImage,
    0
  );
  if (conversationId) {
    await getConversationRef(conversationId).update({
      productStatus: 'accepted',
      status: 'active',
      lastMessage: isNeed
        ? 'Oferta aceita para o anúncio de procura!'
        : 'Oferta aceita!',
      lastMessageSender: sellerId,
      lastMessageType: 'text',
      lastMessageAt: serverTimestamp(),
    });
  }

  const offerDoc = await offersRef().doc(offerId).get();
  const offerData = offerDoc.data() || {};
  const acceptedAmount = Number(offerData.offerAmount || 0);

  createNotification(
    buyerId,
    'offer',
    'Oferta aceita!',
    `Sua oferta de R$ ${acceptedAmount.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} foi aceita para "${productName || 'o que você procura'}"`,
    { productId, offerId }
  );

  return conversationId;
}

export async function rejectOffer(
  offerId,
  productId,
  buyerId,
  sellerId,
  productName
) {
  await updateOfferStatus(offerId, 'rejected');

  if (buyerId) {
    createNotification(
      buyerId,
      'offer',
      'Oferta recusada',
      `Sua oferta para "${productName || 'o anúncio'}" foi recusada pelo vendedor.`,
      { productId, offerId }
    );
  }
}

export async function acceptCounterOffer(
  offerId,
  productId,
  buyerId,
  sellerId,
  productName,
  productImage
) {
  const offerDoc = await offersRef().doc(offerId).get();
  const offerData = offerDoc.data() || {};
  const counterAmount =
    Number(offerData.counterAmount) || Number(offerData.offerAmount);

  await offersRef().doc(offerId).update({
    status: 'accepted',
    offerAmount: counterAmount,
    updatedAt: serverTimestamp(),
  });

  const conversationId = await getOrCreateConversation(
    buyerId,
    sellerId,
    productId,
    productName,
    productImage,
    0
  );
  if (conversationId) {
    await getConversationRef(conversationId).update({
      productStatus: 'accepted',
      status: 'active',
      lastMessage: 'Contraoferta aceita pelo interessado!',
      lastMessageSender: buyerId,
      lastMessageType: 'text',
      lastMessageAt: serverTimestamp(),
    });
  }

  createNotification(
    sellerId,
    'offer',
    'Contraoferta aceita!',
    `O interessado aceitou sua contraproposta de R$ ${counterAmount.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} para "${productName || 'o anúncio'}"`,
    { productId, offerId }
  );

  return conversationId;
}
